import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  inject,
  signal,
} from "@angular/core";
// import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { MatSort } from "@angular/material/sort";
import { MatPaginatorModule, MatPaginatorIntl } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { ActivatedRoute, Router } from "@angular/router";
import {
  MatDialogModule,
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogConfig,
} from "@angular/material/dialog";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { MatSnackBar } from "@angular/material/snack-bar";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { ListUsersService } from "../../../data/user/list-users.service";
import { ListMembersService } from "../../../data/member/list-members.service";
import { ContributorAccessService } from "../../../data/belongs/contributor-access.service";
import { LinkDocumentService } from "../../../data/util/link-document.service";
import {
  DatabaseCacheService,
  Section,
} from "../../../data/cache/database-cache.service";

import dayjs from "dayjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { UserDetailsComponent } from "../user-details/user-details.component";
import { UserActivationComponent } from "../user-activation/user-activation.component";
import { ContributorPickerComponent } from "../contributor-picker/contributor-picker.component";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { TableSkeletonComponent } from "../../../shared/skeleton";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { USER_LIST_I18N } from "./user-list.i18n";
import { COMMON_I18N } from '@app/i18n';

@Component({
  selector: "app-user-list",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./user-list.component.html",
  styleUrls: ["./user-list.component.scss"],
  imports: [...SHARED_IMPORTS, TableSkeletonComponent, PageHeaderComponent],
})
export class UserListComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild("searchInput") searchInput: ElementRef<HTMLInputElement>;

  // MatSort setter pattern: automatically connect sort when table renders
  private _sort: MatSort;
  @ViewChild(MatSort)
  set sort(sort: MatSort) {
    this._sort = sort;
    if (this.dataSource && sort) {
      this.dataSource.sort = sort;
    }
  }
  get sort(): MatSort {
    return this._sort;
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly paginatorIntl = inject(MatPaginatorIntl);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = USER_LIST_I18N;

  filter = ""; //table filter
  isLoading = signal(true);
  readonly searchExpanded = signal(false);

  // section info
  selectedSection: Section;
  users: UserData[] = [];

  contributors = [];

  displayedColumns = [
    "name",
    "status",
    "createdDate",
    "contributors",
    "actions",
  ];

  accountStatus: { [key: string]: string } = {
    OPEN: this.i18n.status.active,
    CLOSE: this.i18n.status.inactive,
    AVAILABLE: this.i18n.status.available,
  };

  dataSource: MatTableDataSource<UserData>;

  constructor(
    public storeUser: StoreUserService,
    private route: ActivatedRoute,
    private listUsersService: ListUsersService,
    private listMembersService: ListMembersService,
    private contributorAccessService: ContributorAccessService,
    private linkDocumentService: LinkDocumentService,
    private common: DatabaseCacheService,
    private router: Router,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private domSanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry,
  ) {
    // カスタムSVG iconの呼び出し
    this.iconRegistry.addSvgIcon(
      "icon_user", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_user.svg",
      ), //必ずサニタイザーを利用して取得する。
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-close", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-close.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-open", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-open.svg",
      ),
    );
  }

  ngOnInit() {
    this.configurePaginatorLabels();
    // section
    this.selectedSection = this.common.getSelectedSection();

    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.selectedSection = this.common.getSelectedSection();
      if (event.type === 'usersListUpdated') {
        this.refreshData();
      }
    });

    this.listUsers();
    this.listContributors();
  }

  applyFilter(event?: Event) {
    if (event) {
      this.filter = (event.target as HTMLInputElement).value;
    }
    const filterValue = this.filter.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  // M3 Expandable Search methods
  expandSearch(): void {
    this.searchExpanded.set(true);
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  collapseSearch(): void {
    if (!this.filter.trim()) {
      this.searchExpanded.set(false);
    }
  }

  onSearchBlur(): void {
    this.collapseSearch();
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.filter = "";
    this.applyFilter();
  }

  refreshData(): void {
    this.listUsers();
    this.listContributors();
  }

  listUsers() {
    this.isLoading.set(true);
    this.users = [];

    //this.listUsersService.listUsers().subscribe(
    this.listUsersService
      .listUserWithUserData(
        this.common.getSelectedSection().sectionId,
        true,
        false,
        false,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.users = [];
          let hasAnonymous = false;

          const users = res as any;

          for (let i = 0; i < users.length; i++) {

            const user = users[i];

            var date = dayjs(user.contractInfo.contractStartAt);
            // var dateStr = (date.isValid()) ? date.format('YYYY/MM/DD [at] HH[h]') : 'ERROR';
            var dateStr = date.isValid() ? date.format("YYYY/MM/DD") : "ERROR";

            let userId = user.user.userId;

            if (userId === "ANONYMOUS") {
              hasAnonymous = true;

              this.users.unshift({
                userId: "--",
                name:
                  user.user.code ??
                  user.user.lastName + " " + user.user.firstName,
                createdDate: dateStr,
                status: "AVAILABLE",
                anonymous: true,
                contributors: null,
                links: null,
              });
            } else {
              this.users.push({
                userId: userId,
                name:
                  user.user.code ??
                  user.user.lastName + " " + user.user.firstName,
                createdDate: dateStr,
                status: user.contractInfo.contractStatus.toUpperCase(),
                anonymous: false,
                contributors: user.contributors,
                links: user.linkDocuments,
              });
            }
          }

          if (!hasAnonymous) {
            this.users.unshift({
              userId: "--",
              name: this.i18n.userName.anonymous,
              createdDate: "--",
              status: "NOT AVAILABLE",
              anonymous: true,
              contributors: null,
              links: null,
            });
          }

          this.dataSource = new MatTableDataSource(this.users);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;

          // Ensure sort works correctly for all columns
          this.dataSource.sortingDataAccessor = (item: UserData, property: string) => {
            switch (property) {
              case 'status':
                // OPEN = 2, CLOSE = 1, others = 0 for proper ordering
                if (item.status === 'OPEN') return 2;
                if (item.status === 'CLOSE') return 1;
                return 0;
              case 'name':
                return item.name.toLowerCase();
              case 'createdDate':
                return item.createdDate;
              default:
                return (item as any)[property];
            }
          };

          this.isLoading.set(false);
        },
        (error) => {
          this.isLoading.set(false);
        },
      );

    this.dataSource = new MatTableDataSource(this.users);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  openUserDetailsDialog(refid): void {
    const dialogRef = this.dialog.open(UserDetailsComponent, {
      width: "740px",
      disableClose: false,
      data: {
        title: this.i18n.dialog.title,
        message: this.i18n.dialog.message,
        id: refid,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {});
  }

  openContributorPicker(userId, userName) {

    const dialogRef = this.dialog.open(ContributorPickerComponent, {
      width: "740px",
      disableClose: false,
      data: {
        title: this.i18n.dialog.title,
        message: this.i18n.dialog.message,
        userId: userId,
        userName: userName,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result != null) {
        this.users
          .filter((u) => u.userId == userId)[0]
          .contributors.push(result);
      }
    });
  }

  openUserActivationDialog(refid): void {
    const dialogRef = this.dialog.open(UserActivationComponent, {
      width: "400px",
      disableClose: false,
      data: {
        title: this.i18n.dialog.title,
        message: this.i18n.dialog.message,
        id: refid,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {});
  }

  createUser() {
    this.router.navigate([
      "/section-admin/",
      this.selectedSection.sectionId,
      "create-user",
    ]);
  }

  updateUser() {
    this.router.navigate(["/update-user"]);
  }

  inviteUser() {
    this.router.navigate(["/invite-user"]);
  }

  setStore(row) {
  }

  listContributors() {
    this.listMembersService
      .listMembers(this.selectedSection.sectionId, "CONTRIBUTOR", true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.contributors = res as any;
      });
  }

  // onItemDropped(event: CdkDragDrop<any>) {

  //   const droppedItem = event.item.data;

  //   let droppedElement = event.container.element.nativeElement;

  //   while (true){
  //     droppedElement = droppedElement.parentElement;
  //   }

  // }

  removeContributorAccess(userId, contributorUserId) {
    const element = document.getElementById(userId + "+" + contributorUserId);

    this.contributorAccessService
      .deleteContributorAccess(userId, contributorUserId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          if (element) {
            element.parentNode.removeChild(element);
          }
        },
        (error) => {
          this.snackBar.open(
            this.i18n.message.removeContributorError +
              JSON.stringify(error.error),
            this.i18n.button.close,
            {
              duration: 5000,
            },
          );
        },
      );
  }

  disableLinkDocument(key) {
    const element = document.getElementById(key);

    this.linkDocumentService
      .updateLinkDocumentStatus(key, "CANCELED")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          if (element) {
            element.parentNode.removeChild(element);
          }
        },
        (error) => {
          this.snackBar.open(
            this.i18n.message.cancelInviteError +
              JSON.stringify(error.error),
            this.i18n.button.close,
            {
              duration: 5000,
            },
          );
        },
      );
  }

  private configurePaginatorLabels(): void {
    this.paginatorIntl.itemsPerPageLabel = COMMON_I18N.paginator.itemsPerPage;
    this.paginatorIntl.nextPageLabel = COMMON_I18N.paginator.nextPage;
    this.paginatorIntl.previousPageLabel = COMMON_I18N.paginator.previousPage;
    this.paginatorIntl.firstPageLabel = COMMON_I18N.paginator.firstPage;
    this.paginatorIntl.lastPageLabel = COMMON_I18N.paginator.lastPage;
    this.paginatorIntl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ) => {
      if (length === 0 || pageSize === 0) {
        return `0 / ${length}`;
      }
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;
      return `${startIndex + 1} – ${endIndex} / ${length}`;
    };
  }
}

export interface UserData {
  userId: string;
  createdDate: string;
  name: string;
  status: string;
  anonymous: boolean;
  contributors: any;
  links: any;
}
